import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Page, PageHeader, Avatar, Label11, Loading, usd, plural, pct } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { KIND_LABELS } from "@/lib/workKinds";
import { money } from "@/pages/owner/ObjectivesMoney";
import {
  CardHead, Cells, Sparkline, Trend, ErrorRows, outageCells, monthName, duration, rate, STATES, STEP_COLS,
} from "@/components/OverviewInsights";
import { cn } from "@/lib/utils";
import { Eye, EyeOff, Lock, Mail, CircleCheck, Clock, Check } from "lucide-react";

// design_handoff_team_roles/screens/Investor.dc.html: an investor's read-only view (GET /investor/dashboard,
// backend/investor_routes.py). Progress and the team always; Money, Plan, Reliability and Delivery times as the
// founders share them. Nothing on this page changes anything but the investor's own weekly email.

const big = (n) => usd(n, Math.abs(n) >= 100 ? 0 : 2);
const signed = (n) => (n > 0 ? `+${usd(n)}` : n < 0 ? `−${usd(-n)}` : usd(0));
const sum = (rows, k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
const and = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);
const STATE_ICON = { done: CircleCheck, review: Eye, "in progress": Clock };

function Card({ title, note, wide, children, label }) {
  return (
    <section aria-label={label || title} className={cn("bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-3.5 min-w-0", wide && "col-span-full")}>
      <CardHead title={title} note={note} />
      {children}
    </section>
  );
}

const NotShared = () => (
  <p className="m-0 flex items-center gap-2 text-muted-foreground"><EyeOff className="w-3.5 h-3.5" aria-hidden="true" />Your founders don't share this.</p>
);
const Empty = ({ children }) => <p className="m-0 text-muted-foreground [text-wrap:pretty]">{children}</p>;

function Progress({ p, short, before }) {
  const total = ["todo", "in progress", "review", "done"].reduce((a, k) => ({ ...a, [k]: sum(p.projects, k) }), {});
  if (!p.projects.length) return <Empty>No objectives yet. They show here once your founders add them.</Empty>;
  const row = (name, r, strong) => (
    <div key={name} className={cn("grid grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))] gap-2 py-[7px] border-t", strong && "font-semibold")}>
      <span>{name}</span>{["todo", "in progress", "review", "done"].map((k) => <span key={k} className="text-end">{r[k]}</span>)}
    </div>
  );
  return <>
    <Cells items={[
      { k: `Done · ${short}`, v: String(p.done), sub: `${p.done_before} in ${before}` },
      { k: `Change reports · ${short}`, v: String(p.reports.count), sub: `${p.reports.accepted} accepted, ${p.reports.waiting} waiting` },
      { k: "Sessions running", v: String(p.running), sub: "Right now" },
    ]} />
    <div className="overflow-x-auto">
      <div className="min-w-[380px] text-sm tabular-nums">
        <div className="grid grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))] gap-2 pb-1.5 text-xs uppercase tracking-[0.09em] text-muted-foreground">
          <span>Objectives</span><span className="text-end">To do</span><span className="text-end">In progress</span><span className="text-end">In review</span><span className="text-end">Done</span>
        </div>
        {p.projects.map((r) => row(r.name, r))}
        {p.projects.length > 1 && row("Company", total, true)}
      </div>
    </div>
    <p className="m-0 text-sm text-muted-foreground">Change report summaries and file names stay with your founders.</p>
  </>;
}

function Team({ t }) {
  return <>
    <ul className="m-0 p-0 list-none flex flex-col">
      {t.people.map((p, i) => (
        <li key={i} className="flex items-center gap-3 py-2 border-t">
          <Avatar name={p.name} size={28} />
          <span className="flex-1 min-w-0 font-semibold truncate">{p.name}</span>
          <span className="text-sm text-muted-foreground text-end">{[p.title, p.work_kind && KIND_LABELS[p.work_kind]].filter(Boolean).join(" · ")}</span>
        </li>
      ))}
    </ul>
    <p className="m-0 text-sm text-muted-foreground">Names, titles and kinds of work only. No emails, no activity.</p>
  </>;
}

function CostChart({ months, fees, current }) {
  const total = (m) => m.spent_usd + (fees ? m.fees_usd || 0 : 0);
  const most = Math.max(...months.map(total), 1);
  const words = months.map((m) => `${monthName(m.month, { month: "short" })} ${big(total(m))}`).join(", ");
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="font-semibold">{fees ? "Total cost per month, last 6 months" : "AI spend per month, last 6 months"}</span>
        <span className="flex gap-3.5 text-sm text-muted-foreground">
          {fees && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-foreground" />Agreed fees</span>}
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-gr-chart-2" />AI spend</span>
        </span>
      </div>
      <div role="img" aria-label={`${fees ? "Total cost" : "AI spend"} per month: ${words}`} className="flex items-end gap-3 h-[214px] px-1 border-b">
        {months.map((m) => {
          const now = m.month === current;
          return (
            <div key={m.month} className="flex-1 min-w-0 h-full flex flex-col items-center justify-end gap-1.5">
              <span className={cn("text-xs tabular-nums", now ? "font-semibold" : "text-muted-foreground")}>{big(total(m))}</span>
              <div className="w-full max-w-[56px] flex flex-col rounded-t overflow-hidden">
                {fees && <div className="bg-foreground" style={{ height: `${((m.fees_usd || 0) / most) * 180}px` }} />}
                <div className="bg-gr-chart-2" style={{ height: `${(m.spent_usd / most) * 180}px` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-3 px-1">
        {months.map((m) => <span key={m.month} className={cn("flex-1 min-w-0 text-center text-sm", m.month === current ? "font-semibold" : "text-muted-foreground")}>{monthName(m.month, { month: "short" })}</span>)}
      </div>
    </div>
  );
}

function Money({ m, month }) {
  const fees = m.fees_shared;
  const rows = m.objectives;
  if (!rows.length) return <Empty>No spend yet. Each objective's budget, AI spend and fee shows here once work starts.</Empty>;
  const panel = (k, list, company) => {
    const [budget, spent, fee, total] = ["budget_usd", "spent_usd", "fees_usd", "total_usd"].map((f) => sum(list, f));
    const left = `${big(Math.max(0, budget - spent))} left this month`;
    const sub = company ? (fees && fee ? `Total cost ${usd(total)} with fees` : left) : fees && fee ? `${big(fee)} in agreed fees` : left;
    return (
      <div key={k} className="flex flex-col gap-1.5 px-3.5 py-3 border border-gr-hairline rounded-lg">
        <Label11>{k}</Label11>
        <span><span className="text-[15px] leading-[22px] font-semibold">{big(spent)}</span><span className="text-muted-foreground"> of {big(budget)} AI budget</span></span>
        <div className="h-1 rounded-sm bg-gr-surface-2 overflow-hidden" role="img" aria-label={`${pct(spent, budget)}% of the AI budget used`}>
          <div className="h-full bg-foreground" style={{ width: `${Math.min(100, pct(spent, budget))}%` }} />
        </div>
        <span className="text-sm text-muted-foreground">{sub}</span>
      </div>
    );
  };
  const cols = fees ? "grid-cols-[minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_repeat(4,minmax(0,0.8fr))]"
    : "grid-cols-[minmax(0,2.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_repeat(3,minmax(0,0.8fr))]";
  return <>
    <div className="grid grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))] gap-3 tabular-nums">
      {m.projects.map((p) => panel(p.name, rows.filter((r) => r.project_id === p.id)))}
      {m.projects.length > 1 && panel("Company", rows, true)}
    </div>
    <div className="overflow-x-auto border border-gr-hairline rounded-lg">
      <div className="min-w-[820px] text-sm tabular-nums">
        <div className={cn("grid gap-3 px-3.5 py-2 text-xs uppercase tracking-[0.09em] text-muted-foreground", cols)}>
          <span>Objective</span><span>Project</span><span>State</span><span className="text-end">Task budget</span><span className="text-end">AI spent</span>
          {fees && <span className="text-end">Fee</span>}<span className="text-end">Total cost</span>
        </div>
        {rows.map((r, i) => {
          const [word, tone] = STATES[r.status] || STATES.todo;
          const Icon = STATE_ICON[r.status];
          return (
            <div key={i} className={cn("grid gap-3 px-3.5 py-2 border-t items-center", cols)}>
              <span className="text-[13px] leading-5">{r.title}</span>
              <span className="text-muted-foreground">{r.project}</span>
              <span className={cn("inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.09em]", tone)}>{Icon && <Icon className="w-3.5 h-3.5" aria-hidden="true" />}{word}</span>
              <span className="text-end">{r.budget_usd ? money(r.budget_usd) : "—"}</span>
              <span className="text-end">{usd(r.spent_usd)}</span>
              {fees && <span className="text-end">{r.fees_usd ? money(r.fees_usd) : "—"}</span>}
              <span className="text-end font-semibold">{usd(r.total_usd)}</span>
            </div>
          );
        })}
        <div className={cn("grid gap-3 px-3.5 py-2.5 border-t bg-gr-surface-2 font-semibold", cols)}>
          <span>{monthName(month)} total</span><span /><span />
          <span className="text-end">{money(sum(rows, "budget_usd"))}</span><span className="text-end">{usd(sum(rows, "spent_usd"))}</span>
          {fees && <span className="text-end">{money(sum(rows, "fees_usd"))}</span>}<span className="text-end">{usd(sum(rows, "total_usd"))}</span>
        </div>
      </div>
    </div>
    <CostChart months={m.months} fees={fees} current={month} />
  </>;
}

const INVOICE_STATE = { paid: ["Paid", "text-gr-success"], refunded: ["Refunded", "text-muted-foreground"], "partly refunded": ["Partly refunded", "text-gr-warning"] };
const amountOf = (i) => (i.amount == null ? "—" : !i.currency || i.currency === "USD" ? usd(i.amount) : `${i.amount.toFixed(2)} ${i.currency}`);

function Plan({ p, month }) {
  const s = p.subscription;
  if (!s && !p.invoices.length && p.balance_usd == null) return <Empty>No plan or invoices yet.</Empty>;
  const when = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const credits = [["Plan credits", p.credits.plan_credits], ["Top-up", p.credits.topups], ["Usage", p.credits.usage],
                   ["Refunds", p.credits.refunds], ...(p.credits.other ? [["Other", p.credits.other]] : [])];
  return <>
    <div className="grid grid-cols-2 gap-3 tabular-nums">
      <div className="flex flex-col gap-0.5 px-3.5 py-3 border border-gr-hairline rounded-lg">
        <Label11>Plan</Label11>
        <span className="text-[15px] leading-[22px] font-semibold">{s?.plan ? [s.plan, s.price].filter(Boolean).join(" · ") : "No plan"}</span>
        <span className="text-sm text-muted-foreground">{s?.ends_at ? `Ends ${when(s.ends_at)}` : s?.renews_at ? `Renews ${when(s.renews_at)}` : "—"}</span>
      </div>
      <div className="flex flex-col gap-0.5 px-3.5 py-3 border border-gr-hairline rounded-lg">
        <Label11>{p.wallet ? "AI wallet" : "AI credits"}</Label11>
        <span className="text-[15px] leading-[22px] font-semibold">{p.balance_usd == null ? "—" : usd(p.balance_usd)}</span>
        <span className="text-sm text-muted-foreground">{p.wallet ? "Credits never expire" : "Plan and top-up credits"}</span>
      </div>
    </div>
    <div className="flex flex-col text-sm tabular-nums">
      <span className="pb-1"><Label11>Credits in {monthName(month)}</Label11></span>
      {credits.map(([k, v]) => <div key={k} className="flex justify-between gap-3 py-1.5 border-t"><span>{k}</span><span>{signed(v)}</span></div>)}
    </div>
    {p.invoices.length > 0 && (
      <div className="flex flex-col text-sm tabular-nums">
        <span className="pb-1"><Label11>Invoices</Label11></span>
        {p.invoices.map((i, n) => {
          const [word, tone] = INVOICE_STATE[i.status] || INVOICE_STATE.paid;
          return (
            <div key={n} className="grid grid-cols-[56px_minmax(0,1fr)_auto_auto] gap-3 py-1.5 border-t items-center">
              <span className="text-muted-foreground">{when(i.date)}</span>
              <span>{i.kind === "topup" ? `Top-up, ${money(i.credits_usd || 0)} of credits` : i.kind === "plan" ? `${i.plan || s?.plan || "Plan"} plan` : i.kind}</span>
              <span className="text-end">{amountOf(i)}</span>
              <span className={cn("inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.09em]", tone)}>{i.status === "paid" && <Check className="w-3.5 h-3.5" aria-hidden="true" />}{word}</span>
            </div>
          );
        })}
      </div>
    )}
    <p className="m-0 text-sm text-muted-foreground">No card details or billing addresses are shared.</p>
  </>;
}

function Reliability({ r }) {
  if (!r.outage_count && !r.turns) return <Empty>No outages or errors yet. This fills in once sessions run.</Empty>;
  return <>
    <Cells items={outageCells(r)} />
    {r.turns > 0 && <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col tabular-nums">
          <Label11>Agent and service errors</Label11>
          <span><span className="text-[24px] leading-7">{rate(r.rate)}</span><span className="text-muted-foreground"> per 100 turns</span></span>
          <Trend r={r} />
        </div>
        <Sparkline trend={r.trend} />
      </div>
      <ErrorRows errors={r.errors} />
    </>}
  </>;
}

function Delivery({ d }) {
  if (!d.objectives.length) return <Empty>No delivery times yet. Each objective shows its times once work starts.</Empty>;
  const before = monthName(d.previous_month);
  const was = (v) => (v == null ? `None in ${before}` : `${duration(v)} in ${before}`);
  const [company, ...projects] = d.medians;
  const MEDIAN_COLS = "grid grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] gap-3 px-3.5 py-2";
  return <>
    <Cells big items={[
      { k: "Time to start", v: duration(company.now.start), sub: was(company.before.start) },
      { k: "Time to first report", v: duration(company.now.first_report), sub: was(company.before.first_report) },
      { k: "Time to done", v: duration(company.now.done), sub: was(company.before.done) },
      { k: "Founder review time", v: duration(d.review), sub: `Company median · ${d.review_before == null ? `none in ${before}` : `${duration(d.review_before)} in ${before}`}` },
    ]} />
    <div className="overflow-x-auto border border-gr-hairline rounded-lg">
      <div className="min-w-[640px] text-sm tabular-nums">
        <div className={cn(MEDIAN_COLS, "text-xs uppercase tracking-[0.09em] text-muted-foreground")}>
          <span>Medians</span><span className="text-end">To start</span><span className="text-end">To first report</span><span className="text-end">To done</span>
        </div>
        {[...projects, company].map((p) => (
          <div key={p.project_id || "company"} className={cn(MEDIAN_COLS, "border-t", !p.project_id && "font-semibold")}>
            <span className="text-[13px] leading-5">{p.name}</span>
            {["start", "first_report", "done"].map((k) => (
              <span key={k} className="text-end">{duration(p.now[k])} <span className="font-normal text-muted-foreground">· {duration(p.before[k])}</span></span>
            ))}
          </div>
        ))}
      </div>
    </div>
    <div className="overflow-x-auto border border-gr-hairline rounded-lg">
      <div className="min-w-[760px] text-sm tabular-nums">
        <div className={cn(STEP_COLS, "text-xs uppercase tracking-[0.09em] text-muted-foreground")}>
          <span>Objective</span><span>Project</span><span>State</span><span className="text-end">To start</span>
          <span className="text-end">To first report</span><span className="text-end">To done</span>
        </div>
        {d.objectives.map((o) => {
          const [word, tone] = STATES[o.status] || STATES.todo;
          return (
            <div key={o.id} className={cn(STEP_COLS, "border-t items-center")}>
              <span className="flex flex-col"><span className="text-[13px] leading-5">{o.title}</span>
                {o.before_release && <span className="text-muted-foreground">From before the release</span>}</span>
              <span className="text-muted-foreground">{o.project || "No project"}</span>
              <span className={cn("text-xs uppercase tracking-[0.09em]", tone)}>{word}</span>
              <span className="text-end">{duration(o.start)}</span><span className="text-end">{duration(o.first_report)}</span><span className="text-end">{duration(o.done)}</span>
            </div>
          );
        })}
      </div>
    </div>
    <p className="m-0 text-sm text-muted-foreground">Founder review time is the time from a report being ready to a founder opening it. No person's name is shown next to a time.</p>
  </>;
}

function WeeklyEmail({ on }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: async (v) => (await api.put("/investor/weekly-email", { on: v })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["investor"] }); toast.success(d.weekly_email ? "You'll get the summary on Mondays." : "No more Monday summaries."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const value = save.isPending ? save.variables : on;
  return (
    <label id="weekly-email" className="flex items-center gap-2.5 text-sm cursor-pointer scroll-mt-4">
      <Switch checked={value} onCheckedChange={(v) => save.mutate(v)} data-testid="investor-weekly" />Weekly summary email on Mondays
    </label>
  );
}

export default function InvestorDashboard() {
  const { data: d, isLoading } = useQuery({ queryKey: ["investor", "dashboard"], refetchInterval: 60000,
                                          queryFn: async () => (await api.get("/investor/dashboard")).data });
  const action = <Button asChild variant="outline"><Link to="/investor/messages"><Mail />Write to your founders</Link></Button>;
  if (isLoading || !d) return <Page><PageHeader eyebrow="Investor view" title="Investor view" actions={action} /><Loading /></Page>;

  const share = d.sharing;
  const short = monthName(d.month, { month: "short" }).toUpperCase();
  const before = monthName(d.previous_month);
  const rows = d.money?.objectives || [];
  const [budget, spent] = [sum(rows, "budget_usd"), sum(rows, "spent_usd")];
  const done = d.progress.done;
  const status = !d.progress.projects.length ? "No objectives done yet. Your founders are setting up."
    : `${plural(done, "objective")} done this month${d.money ? `, ${big(spent)} of ${big(budget)} AI budget spent` : ""}.`;
  const counts = d.team.counts;
  const teamLine = `${plural(d.team.people.length, "person", "people")}: ${and([
    counts.Founder && plural(counts.Founder, "founder"), counts["Team member"] && plural(counts["Team member"], "team member"),
    counts["Independent professional"] && plural(counts["Independent professional"], "independent professional"),
  ].filter(Boolean))}`;
  const doneTimes = d.delivery?.medians?.[0];
  const off = { v: "—", sub: "Your founders don't share this." };
  return (
    <Page data-testid="investor-dashboard">
      <PageHeader eyebrow={[d.company_name, "Investor view"].filter(Boolean).join(" · ")} title={`How ${d.company_name || "the company"}'s work is going.`}
        status={status} actions={action} />
      <p className="m-0 -mt-2 flex items-start gap-2 text-sm text-muted-foreground"><Eye className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
        <span>View only, {monthName(d.month, { month: "long", year: "numeric" })}. Your founders choose what's shared. Times and money are per objective and project, never per person.</span></p>
      <div className="flex flex-wrap gap-px bg-gr-hairline rounded-[10px] overflow-hidden tabular-nums">
        {[
          { k: `Objectives done · ${short}`, v: String(done), sub: `${d.progress.done_before} in ${before}` },
          d.money ? { k: `AI spend · ${short}`, v: big(spent), sub: `of ${big(budget)} budget` } : { k: `AI spend · ${short}`, ...off },
          d.reliability ? { k: "Errors per 100 turns", v: rate(d.reliability.rate), sub: d.reliability.previous_rate == null ? `None in ${before}` : `${rate(d.reliability.previous_rate)} in ${before}` }
            : { k: "Errors per 100 turns", ...off },
          d.delivery ? { k: "Median time to done", v: duration(doneTimes.now.done), sub: doneTimes.before.done == null ? `None in ${before}` : `${duration(doneTimes.before.done)} in ${before}` }
            : { k: "Median time to done", ...off },
        ].map((c) => (
          <div key={c.k} className="flex-[1_1_180px] bg-card px-5 py-3.5 flex flex-col gap-0.5">
            <Label11>{c.k}</Label11><span className="text-[24px] leading-7">{c.v}</span><span className="text-sm text-muted-foreground">{c.sub}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(440px,100%),1fr))] gap-4 items-start">
        <Card title="Progress" note="Objectives and change reports"><Progress p={d.progress} short={short} before={before} /></Card>
        <Card title="Team" note={teamLine}><Team t={d.team} /></Card>
        <Card title="Money per objective" wide note={!share.money ? "" : share.hiring_costs ? `${monthName(d.month)}, US dollars. Total cost = fee + AI spend.` : `${monthName(d.month)}, US dollars. Hiring costs are not shared.`}>
          {d.money ? <Money m={d.money} month={d.month} /> : <NotShared />}
        </Card>
        <Card title="Plan, credits and invoices" note="Read only">{d.plan ? <Plan p={d.plan} month={d.month} /> : <NotShared />}</Card>
        <Card title="Reliability" note="Servers, the AI agent and AI providers. Never people.">{d.reliability ? <Reliability r={d.reliability} /> : <NotShared />}</Card>
        <Card title="Delivery times, per objective" label="Delivery times" wide note={`Medians for ${monthName(d.month)}, ${before} in grey`}>
          {d.delivery ? <Delivery d={d.delivery} /> : <NotShared />}
        </Card>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <p className="m-0 flex items-center gap-2 text-sm text-muted-foreground"><Lock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />You never see sessions, change report summaries or your founders' messages with the team.</p>
        <WeeklyEmail on={d.weekly_email} />
      </div>
    </Page>
  );
}
